import { useMemo, useRef, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const ACCENTS = ["#38BDF8", "#00E5FF", "#60A5FA", "#F59E0B", "#4ADE80", "#A3E635"];

const Starfield = ({ count, size, color, radius, speed, opacity }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = radius * (0.35 + Math.random() * 0.65);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.7;
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count, radius]);

  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.y = state.clock.elapsedTime * speed;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        color={color}
        transparent
        opacity={opacity}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

const OrbitNodes = () => {
  const nodes = useRef([]);
  const linesRef = useRef();
  const linePositions = useMemo(() => new Float32Array(6 * 2 * 3), []);
  const config = useMemo(
    () =>
      ACCENTS.map((c, i) => ({
        color: c,
        offset: (i / 6) * Math.PI * 2,
        radius: 3.1 + (i % 2) * 0.55,
        speed: 0.22 + (i % 3) * 0.07,
        tilt: i % 2 === 0 ? 0.35 : -0.28,
      })),
    []
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    config.forEach((c, i) => {
      const a = t * c.speed + c.offset;
      const x = Math.cos(a) * c.radius;
      const z = Math.sin(a) * c.radius;
      const y = Math.sin(a) * c.radius * c.tilt;
      const m = nodes.current[i];
      if (m) m.position.set(x, y, z);
      linePositions[i * 6] = 0;
      linePositions[i * 6 + 1] = 0;
      linePositions[i * 6 + 2] = 0;
      linePositions[i * 6 + 3] = x;
      linePositions[i * 6 + 4] = y;
      linePositions[i * 6 + 5] = z;
    });
    if (linesRef.current) {
      linesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <group>
      {config.map((c, i) => (
        <group key={i} ref={(el) => (nodes.current[i] = el)}>
          <mesh>
            <sphereGeometry args={[0.09, 16, 16]} />
            <meshBasicMaterial color={c.color} />
          </mesh>
          <mesh>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshBasicMaterial color={c.color} transparent opacity={0.18} />
          </mesh>
        </group>
      ))}
      <lineSegments ref={linesRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={12} array={linePositions} itemSize={3} />
        </bufferGeometry>
        <lineBasicMaterial color="#00e5ff" transparent opacity={0.22} blending={THREE.AdditiveBlending} />
      </lineSegments>
    </group>
  );
};

const Core = () => {
  const outer = useRef();
  const mid = useRef();
  const inner = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (outer.current) {
      outer.current.rotation.x = t * 0.1;
      outer.current.rotation.y = t * 0.14;
    }
    if (mid.current) {
      mid.current.rotation.x = -t * 0.2;
      mid.current.rotation.z = t * 0.16;
    }
    if (inner.current) {
      const s = 1 + Math.sin(t * 1.4) * 0.12;
      inner.current.scale.set(s, s, s);
    }
  });

  return (
    <group>
      <mesh ref={outer}>
        <icosahedronGeometry args={[1.9, 1]} />
        <meshBasicMaterial color="#0ea5e9" wireframe transparent opacity={0.35} />
      </mesh>
      <mesh ref={mid}>
        <octahedronGeometry args={[1.15, 0]} />
        <meshBasicMaterial color="#00e5ff" wireframe transparent opacity={0.5} />
      </mesh>
      <mesh ref={inner}>
        <sphereGeometry args={[0.42, 32, 32]} />
        <meshBasicMaterial color="#7dd3fc" transparent opacity={0.95} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.62, 24, 24]} />
        <meshBasicMaterial color="#00e5ff" transparent opacity={0.14} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} rotation={[Math.PI / 2 + i * 0.5, i * 0.7, 0]}>
          <torusGeometry args={[2.6 + i * 0.55, 0.005, 8, 128]} />
          <meshBasicMaterial
            color={i === 1 ? "#007aff" : "#00e5ff"}
            transparent
            opacity={0.3 - i * 0.06}
          />
        </mesh>
      ))}
    </group>
  );
};

const Rig = ({ children }) => {
  const group = useRef();

  useFrame((state) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    const scroll = Math.min(window.scrollY / window.innerHeight, 1);
    group.current.rotation.y = THREE.MathUtils.lerp(
      group.current.rotation.y,
      state.pointer.x * 0.3 + t * 0.03,
      0.05
    );
    group.current.rotation.x = THREE.MathUtils.lerp(
      group.current.rotation.x,
      state.pointer.y * 0.2,
      0.05
    );
    group.current.position.z = THREE.MathUtils.lerp(group.current.position.z, scroll * 2.6, 0.06);
    group.current.position.x = THREE.MathUtils.lerp(
      group.current.position.x,
      2.2 + state.pointer.x * 0.4,
      0.05
    );
  });

  return (
    <group ref={group} position={[2.2, 0, 0]}>
      {children}
    </group>
  );
};

export const HeroScene = () => (
  <div className="absolute inset-0" data-testid="hero-3d-canvas">
    <Canvas camera={{ position: [0, 0, 9], fov: 55 }} dpr={[1, 1.75]} gl={{ antialias: true, alpha: true }}>
      <Suspense fallback={null}>
        <Starfield count={2200} size={0.028} color="#38bdf8" radius={17} speed={0.012} opacity={0.6} />
        <Starfield count={900} size={0.05} color="#00e5ff" radius={14} speed={-0.018} opacity={0.5} />
        <Starfield count={500} size={0.02} color="#ffffff" radius={20} speed={0.008} opacity={0.8} />
        <Rig>
          <Core />
          <OrbitNodes />
        </Rig>
        <fog attach="fog" args={["#020617", 11, 30]} />
      </Suspense>
    </Canvas>
  </div>
);
